import { Box, styled, Typography, Button } from "@mui/material";

export const StyledMainBox = styled(Box)(({ theme }) => ({
  border: "1px solid #d0e4f7",
  borderRadius: "14px",
  width: "100%",
  height: "100%",
  background: "#ffffff",
  boxShadow: "0px 4px 16px rgba(11, 101, 181, 0.12)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  transition: "box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    boxShadow: "0px 8px 28px rgba(11, 101, 181, 0.22)",
    transform: "translateY(-2px)",
  },
}));

// ── Card Header (dark gradient) ──────────────────────────────────────────────
export const StyledCardHeader = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #0B2373 0%, #0B65B5 100%)",
  padding: "12px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "8px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}));

export const StyledTitle = styled(Typography)(() => ({
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: 1.4,
  letterSpacing: "0.01em",
  minWidth: 0,
  overflowWrap: "anywhere",
  wordBreak: "normal",
  flex: 1,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
}));

export const StyledHeaderRight = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  marginLeft: "auto",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "flex-start",
    marginLeft: 0,
  },
}));

export const StyledQuantity = styled(Typography)(() => ({
  color: "#0B65B5",
  fontSize: "11px",
  fontWeight: 700,
  background: "#ffffff",
  borderRadius: "20px",
  padding: "2px 10px",
  display: "inline-block",
  whiteSpace: "nowrap",
}));

// ── Chip row below header ────────────────────────────────────────────────────
export const StyledBoxFlex = styled(Box)(({ pad }) => ({
  display: "flex",
  justifyContent: pad ? "flex-start" : "space-between",
  alignItems: "center",
  flexWrap: pad ? "wrap" : "nowrap",
  gap: pad ? "6px" : "0px",
  padding: pad ? "8px 12px" : "0px",
  background: pad ? "#f0f6ff" : "transparent",
  borderBottom: pad ? "1px solid #d0e4f7" : "none",
}));

// ── Info rows ────────────────────────────────────────────────────────────────
export const StyledOrderDetailsBox = styled(Box)(() => ({
  padding: "14px 16px 10px",
  flex: 1,
}));

export const StyledBox = styled(Box)(() => ({
  paddingTop: "10px",
  display: "flex",
  flexDirection: "column",
  gap: "7px",
}));

// shared row used for info label/value pairs
export const StyledRowFlex = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "8px",
  flexWrap: "wrap",
}));

export const SenderLabel = styled(Typography)(() => ({
  color: "#6b7a99",
  fontSize: "11.5px",
  fontWeight: 600,
  minWidth: "90px",
  flexShrink: 0,
}));

export const SenderValue = styled(Typography)(({ theme }) => ({
  color: "#1a2340",
  fontSize: "11.5px",
  fontWeight: 600,
  textAlign: "end",
  wordBreak: "break-word",
  minWidth: 0,
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    textAlign: "start",
  },
}));

export const StyledSpan = styled("span")(() => ({
  fontWeight: 700,
}));

// ── Image / action footer ────────────────────────────────────────────────────
export const StyledLockerActionBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 16px",
  gap: "12px",
  overflow: "visible",
  background: "#f4f8fd",
  borderTop: "1px solid #d0e4f7",
  [theme.breakpoints.down("xs")]: {
    flexDirection: "column",
  },
}));

export const StyledImageContainer = styled(Box)(() => ({
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  flexWrap: "nowrap",
  flexShrink: 1,
  minWidth: 0,
  paddingBottom: "2px",
}));

export const StyledNoImageBox = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 64,
}));

export const StyledNoImageText = styled(Typography)(() => ({
  fontSize: "12px",
  color: "#9C9C9C",
  fontStyle: "italic",
}));

export const StyledBoxStatus = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  flexShrink: 0,
}));

export const StyledTypography = styled(Typography)(() => ({
  color: "#8a9ab5",
  fontSize: "12px",
  fontWeight: 500,
  padding: "5px 16px",
  fontStyle: "italic",
}));

// ── Note button ──────────────────────────────────────────────────────────────
export const StyledNoteButton = styled(Box)(() => ({
  fontSize: "11px",
  fontWeight: 600,
  color: "#0B65B5",
  cursor: "pointer",
  background: "#ffffff",
  border: "1.5px solid #0B65B5",
  borderRadius: "20px",
  padding: "3px 10px",
  whiteSpace: "nowrap",
  "&:hover": { background: "#e8f1fb" },
}));

export const StyledChipRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
  minWidth: 0,
}));

// ── Note modal ───────────────────────────────────────────────────────────────
export const StyledNoteModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: theme.palette.custom.white,
  borderRadius: "14px",
  width: 520,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "85vh",
  outline: "none",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

export const StyledNoteModalHeader = styled(Box)(() => ({
  background: "linear-gradient(135deg, #0B2373 0%, #0B65B5 100%)",
  padding: "14px 20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const StyledNoteModalTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.white,
  fontWeight: 700,
  fontSize: "16px",
}));

export const StyledNoteModalContent = styled(Box)(() => ({
  padding: "20px 24px",
  flex: 1,
  overflowY: "auto",
}));

export const StyledInvoiceUploadBox = styled(Box)(({ hasfile }) => ({
  border: `2px dashed ${hasfile ? "#00C046" : "#d0e4f7"}`,
  borderRadius: "10px",
  padding: "20px",
  textAlign: "center",
  background: hasfile ? "#f0fff6" : "#f7faff",
  cursor: "pointer",
  transition: "border-color 0.2s, background 0.2s",
  "&:hover": {
    borderColor: hasfile ? "#00C046" : "#126ABC",
  },
}));

export const StyledInvoiceFileName = styled(Typography)(({ hasfile }) => ({
  fontSize: "13px",
  fontWeight: 600,
  color: hasfile ? "#065F2E" : "#126ABC",
}));

export const StyledInvoiceHint = styled(Typography)(() => ({
  fontSize: "11px",
  color: "#9C9C9C",
  marginTop: "4px",
}));

export const StyledInvoiceModalDesc = styled(Typography)(() => ({
  fontSize: "13px",
  color: "#6b7a99",
  marginBottom: "16px",
}));

export const StyledInvoiceButtonRow = styled(Box)(() => ({
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
  marginTop: "24px",
}));

export const StyledInvoiceCancelButton = styled(Button)(() => ({
  textTransform: "none",
  borderRadius: "8px",
  padding: "6px 24px",
}));

export const StyledInvoiceSubmitButton = styled(Button)(() => ({
  textTransform: "none",
  borderRadius: "8px",
  padding: "6px 24px",
  background: "#126ABC",
  color: "#fff",
  "&:hover": { background: "#0f5a9e" },
  "&.Mui-disabled": { background: "#9BBEE4", color: "#fff" },
}));
export const StyledNoteText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.base.fontSize,
  color: theme.palette.custom.blackColor,
  lineHeight: 1.7,
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
}));
